// Drag and Drop Helper Utilities
// Pure functions for calculating drag-and-drop operations

import { closestCenter } from '@dnd-kit/core';
import { getTaskAndSubtaskIds } from './taskUtils';
import { DND_SENTINEL_BEFORE_FIRST, DND_SENTINEL_AFTER_LAST } from './dndConstants';

/**
 * Custom collision detection that excludes the active (dragged) item.
 * Uses center of dragged item (closestCenter) - can make it hard to drop at list start/end.
 */
export function createCollisionDetection() {
  return (args) => {
    const { active, collisionRect, droppableRects, droppableContainers, pointerCoordinates } = args;
    
    // Get all collisions using the default algorithm
    const collisions = closestCenter({
      ...args,
      collisionRect,
      droppableRects,
      droppableContainers,
      pointerCoordinates
    });
    
    // Filter out the active item from collisions
    return collisions.filter(collision => collision.id !== active.id);
  };
}

function isPointerInRect(pointer, rect) {
  if (!pointer || !rect) return false;
  return (
    pointer.x >= rect.left &&
    pointer.x <= rect.left + rect.width &&
    pointer.y >= rect.top &&
    pointer.y <= rect.top + rect.height
  );
}

/**
 * Pointer-based collision detection. Prefers list sentinels when the pointer is
 * inside their rect (so the drop indicator works without leaving the container).
 * When the pointer is over multiple droppables (e.g. parent row and nested subtask),
 * prefers the innermost (smallest area) so dropping into a subtask list works.
 * Otherwise returns the droppable whose center is closest to the pointer.
 */
export function createPointerCollisionDetection() {
  return (args) => {
    const { active, droppableRects, pointerCoordinates } = args;
    if (!pointerCoordinates) return [];

    // Prefer sentinels when pointer is within their rect so drop-at-start/end works immediately
    for (const id of [DND_SENTINEL_BEFORE_FIRST, DND_SENTINEL_AFTER_LAST]) {
      if (id === active.id) continue;
      const rect = droppableRects.get(id);
      if (rect && isPointerInRect(pointerCoordinates, rect)) {
        return [{ id }];
      }
    }

    // Find all droppables that contain the pointer (e.g. parent task and its subtasks)
    const containing = [];
    for (const [id, rect] of droppableRects.entries()) {
      if (id === active.id) continue;
      if (rect && isPointerInRect(pointerCoordinates, rect)) {
        const area = rect.width * rect.height;
        containing.push({ id, area });
      }
    }
    // Prefer innermost: smallest area = the actual task row under the pointer (e.g. subtask, not parent)
    if (containing.length > 0) {
      containing.sort((a, b) => a.area - b.area);
      return [{ id: containing[0].id }];
    }

    // Fallback: closest center (e.g. when pointer is between items)
    let closest = null;
    let minDistance = Infinity;
    for (const [id, rect] of droppableRects.entries()) {
      if (id === active.id) continue;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distance = Math.hypot(
        pointerCoordinates.x - centerX,
        pointerCoordinates.y - centerY
      );
      if (distance < minDistance) {
        minDistance = distance;
        closest = { id };
      }
    }
    return closest ? [{ id: closest.id }] : [];
  };
}

/**
 * Calculates drop position (before or after) based on mouse position
 * @param {Object} over - The drop target from dnd-kit
 * @param {Object} active - The dragged item from dnd-kit
 * @param {number|null} currentMouseY - Current mouse Y position
 * @returns {string} - 'before' or 'after'
 */
export function calculateDropPosition(over, active, currentMouseY) {
  if (!over || !over.rect) {
    return 'after'; // Default
  }

  const overRect = over.rect;
  const overCenterY = overRect.top + overRect.height / 2;
  
  // Try multiple methods to get pointer position
  let pointerY = null;
  
  // Method 1: Use tracked mouse position (most accurate)
  if (currentMouseY !== null) {
    pointerY = currentMouseY;
  }
  // Method 2: Use active rect top (where the dragged item is)
  else if (active && active.rect) {
    pointerY = active.rect.top;
  }
  
  // Calculate position based on pointer relative to center
  if (pointerY !== null) {
    return pointerY < overCenterY ? 'before' : 'after';
  }
  
  // Fallback
  return 'after';
}

/**
 * Checks if a drop target is valid (not a subtask of the dragged task)
 * @param {Object} draggedTask - The task being dragged
 * @param {Object} dropTargetTask - The task being dropped on
 * @param {Array} tasks - All tasks
 * @returns {boolean} - True if drop is valid
 */
export function isValidDropTarget(draggedTask, dropTargetTask, tasks) {
  if (!draggedTask || !dropTargetTask) {
    return false;
  }
  
  // Prevent dropping on a subtask of the dragged task
  const draggedTaskAndSubtaskIds = getTaskAndSubtaskIds(draggedTask.id, tasks);
  return !draggedTaskAndSubtaskIds.includes(dropTargetTask.id);
}

